import { useState } from 'react';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { STATUS_PAGE } from '../catalog/component/serviceStatus';
import { visuallyHidden } from '../visuallyHidden';
import { ComponentsCard } from './ComponentsCard';
import {
  bySeverity,
  groupIncidents,
  formatBerlinTime,
  formatLength,
  incidentHref,
  incidentName,
  minutesBetween,
  SEVERITY_LABEL,
  severityColour,
  stateLabel,
  useStatusOverviews,
  type IncidentGroup,
  type Visibility,
} from './overview';
import { IncidentsCard, MaintenanceCard, SlaCard } from './SideCards';

const VIEWS: { id: Visibility; label: string }[] = [
  { id: 'public', label: 'Customer-facing' },
  { id: 'all', label: 'Internal' },
];

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  bar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  // Every control here is a plain outline: the portal declares nothing and
  // publishes nothing itself, and a filled button would read as if it did.
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 32,
    padding: theme.spacing(0, 1.5),
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text)',
    font: 'inherit',
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': { borderColor: 'var(--ui-primary)', textDecoration: 'none' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
    '&[aria-pressed="true"]': {
      borderColor: 'var(--ui-primary)',
      background: 'var(--ui-nav-active-bg)',
      color: 'var(--ui-primary)',
      fontWeight: 600,
    },
  },
  views: { display: 'flex', gap: theme.spacing(0.75) },
  banner: {
    display: 'grid',
    gridTemplateColumns: '10px minmax(0, 1fr) auto',
    alignItems: 'start',
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    border: '1px solid var(--ui-warn-border)',
    borderRadius: 8,
    background: 'var(--ui-warn-bg)',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '10px minmax(0, 1fr)',
    },
  },
  // A major outage is red rather than the yellow of a degradation, so the
  // banner that matters most cannot be mistaken for one that matters less.
  bannerMajor: {
    borderColor: 'var(--ui-danger-border)',
    background: 'var(--ui-danger-bg)',
    '& $bannerTitle': { color: 'var(--ui-danger-text)' },
  },
  bannerDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginTop: '0.4em',
  },
  bannerTitle: {
    fontWeight: 600,
    color: 'var(--ui-warn-text)',
    overflowWrap: 'anywhere',
  },
  bannerText: { fontSize: '0.875rem', overflowWrap: 'anywhere' },
  bannerAction: {
    [theme.breakpoints.down('xs')]: { gridColumn: 2, justifySelf: 'start' },
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 22rem)',
    alignItems: 'start',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  side: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  foot: { fontSize: '0.75rem', color: 'var(--ui-text-muted)' },
  alike: {
    marginLeft: theme.spacing(0.75),
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  hidden: visuallyHidden,
  more: {
    fontSize: '0.875rem',
    color: 'var(--ui-text-muted)',
    '& a': { color: 'var(--ui-primary)', fontWeight: 600 },
  },
}));

/**
 * How many incidents stand as banners. Past a few, banners stop drawing the
 * eye to what is open and only push the components off the screen; the rest
 * are counted beneath them and listed in the incidents card.
 */
const MAX_BANNERS = 3;

/**
 * One ongoing incident, or several alike, above everything else
 * (specs/status-page.md AC-3): how long it has run is the oldest's; its
 * state, its latest update and the incident it opens are the one heard from
 * last, so a fresh outage of an old name does not read as the old, settled
 * one.
 */
const IncidentBanner = ({
  group: { oldest, current, count },
  now,
}: {
  group: IncidentGroup;
  now: number;
}) => {
  const classes = useStyles();
  const what = incidentName(current);
  const major = current.severity === 'major';
  const linear =
    current.linearIssueUrl ?? (count > 1 ? oldest.linearIssueUrl : null);
  return (
    <article
      className={`${classes.banner} ${major ? classes.bannerMajor : ''}`}
      aria-label={count > 1 ? `${what}, ${count} open` : what}
    >
      <span
        className={classes.bannerDot}
        style={{ background: severityColour(current.severity) }}
        aria-hidden
      />
      <div>
        <div className={classes.bannerTitle}>
          {what}
          {count > 1 && (
            <span className={classes.alike}>
              <span aria-hidden title={`${count} open incidents alike`}>
                ×{count}
              </span>
              <span className={classes.hidden}>, {count} open</span>
            </span>
          )}
        </div>
        {current.latestUpdate && (
          <div className={classes.bannerText}>{current.latestUpdate.body}</div>
        )}
        <div className={classes.bannerText}>
          {SEVERITY_LABEL[current.severity] ?? SEVERITY_LABEL.minor} ·{' '}
          {stateLabel(current.state)} · started{' '}
          {formatLength(minutesBetween(oldest.startedAt, now))} ago
          {!current.public && ' · internal'}
          {linear && (
            <>
              {' · '}
              <Link to={linear}>Linear issue</Link>
            </>
          )}
        </div>
      </div>
      <Link
        to={incidentHref(current)}
        className={`${classes.chip} ${classes.bannerAction}`}
      >
        Open incident
      </Link>
    </article>
  );
};

/**
 * The Status page (DX-309; promised in specs/status-page.md): the internal
 * view of status.revenexx.com — every service by group with a bar per day,
 * the ongoing incidents as banners, the recent ones, the planned maintenance
 * and the month's SLA.
 *
 * It is a second view of the status service's data and keeps nothing of its
 * own (docs/status-probes.md): every figure is the status service's answer,
 * both views of which are asked up front, and every action leads there — the
 * portal declares no incident and publishes nothing. While the status service
 * does not answer, the page says so and shows no figure.
 */
export const StatusPage = () => {
  const classes = useStyles();
  const overviews = useStatusOverviews();
  const [view, setView] = useState<Visibility>('public');
  const shown = overviews[view];
  const now = Date.now();

  let body: JSX.Element;
  if (shown.loading) {
    body = <Progress />;
  } else if (shown.error || !shown.data) {
    body = (
      <Typography role="alert">
        Couldn't load the status from status.revenexx.com, so nothing is shown
        here. Its public page, linked above, may still answer.
      </Typography>
    );
  } else {
    const overview = shown.data;
    const openGroups = groupIncidents(bySeverity(overview.incidents.open));
    const notBannered = openGroups
      .slice(MAX_BANNERS)
      .reduce((sum, group) => sum + group.count, 0);
    body = (
      <>
        {overview.incidents.open.length > 0 && (
          <section className={classes.stack} aria-label="Ongoing incidents">
            {openGroups.slice(0, MAX_BANNERS).map(group => (
              <IncidentBanner key={group.oldest.id} group={group} now={now} />
            ))}
            {notBannered > 0 && (
              <Typography className={classes.more}>
                <a href="#status-incidents">
                  {notBannered} more ongoing{' '}
                  {notBannered === 1 ? 'incident' : 'incidents'}
                </a>
                , listed under Incidents.
              </Typography>
            )}
          </section>
        )}
        <div className={classes.columns}>
          <ComponentsCard
            groups={overview.groups}
            windowDays={overview.windowDays}
            customerFacing={view === 'public'}
          />
          <div className={classes.side}>
            <IncidentsCard
              open={openGroups}
              recent={overview.incidents.recent}
              windowDays={overview.windowDays}
              historyUrl={overview.links.incidents}
              now={now}
            />
            <MaintenanceCard maintenance={overview.maintenance} />
            <SlaCard
              sla={overview.sla}
              internalView={view === 'all'}
              reportUrl={overview.links.slaReport}
            />
          </div>
        </div>
        <Typography className={classes.foot}>
          Read from status.revenexx.com at{' '}
          {formatBerlinTime(overview.generatedAt)}, and again every minute while
          this page is open.
        </Typography>
      </>
    );
  }

  const links = shown.data?.links;
  return (
    <Page themeId="home">
      <Header
        title="Status"
        subtitle="Live probes, incidents and maintenance — the internal view of status.revenexx.com."
      />
      <Content>
        <div className={classes.stack}>
          <div className={classes.bar}>
            <div className={classes.views} role="group" aria-label="View">
              {VIEWS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={classes.chip}
                  aria-pressed={view === id}
                  onClick={() => setView(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className={classes.actions}>
              <Link
                to={links?.publicPage ?? STATUS_PAGE}
                className={classes.chip}
              >
                Public page
              </Link>
              {links && (
                <Link to={links.declareIncident} className={classes.chip}>
                  Declare incident
                </Link>
              )}
            </div>
          </div>
          {body}
        </div>
      </Content>
    </Page>
  );
};
